import type WebApiClient from "../../api/WebApiClient.js";
import { buildCopyFlagsMessage } from "../../app-utils/alert.js";
import { notify } from "../../modules/notifications/notify.js";
import { loadUserParams } from "./auth.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type Project, type RegionFlag } from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import {
  COPY_FLAGS_FAILURE,
  COPY_FLAGS_REQUEST,
  COPY_FLAGS_SUCCESS,
  DELETE_FLAGS_FAILURE,
  DELETE_FLAG_REQUEST,
  DELETE_SAVED_ITEM_SUCCESS,
} from "@/redux/action-types.ts";
import { ENTITY_TYPE } from "@/settings/settings.ts";

export function deleteFlags(flagIds: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<RegionFlag[]> => {
    dispatch({
      type: DELETE_FLAG_REQUEST,
      payload: flagIds,
    });

    return webApiClient
      .deleteFlagList(flagIds)
      .then((response: RegionFlag[]) => {
        const deletedFlagIds: number[] = response.filter((f) => f.deleted).map((f) => f.id);
        dispatch({
          type: DELETE_SAVED_ITEM_SUCCESS,
          payload: {
            deletedIds: deletedFlagIds,
            type: ENTITY_TYPE.REGION_FLAG,
          },
        });

        notify.info(
          {
            title: selMessage({
              id: "flag.deleted.successTitle",
              values: { flagsCount: deletedFlagIds.length },
            }),
            children: selMessage({
              id: "flag.deleted.successMessage",
              values: { flagsCount: deletedFlagIds.length },
            }),
          },
          { autoClose: 10000 },
        );

        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_FLAGS_FAILURE,
          alertTitle: selMessage({
            id: "flag.deleted.errorTitle",
          }),
          alertMessage: selMessage({
            id: "flag.deleted.errorMessage",
            values: { flagsCount: flagIds.length },
          }),
          throwError: true, // to enter the rejected branch of the promise in case of errors
        }),
      );
  };
}

function initiateCopyFlagsRequest(flagIds: number[], project: Project, isPrivate: boolean) {
  return {
    type: COPY_FLAGS_REQUEST,
    payload: {
      flagIds,
      project,
      isPrivate,
    },
  };
}

function copyFlagsRequestSuccess(response: boolean) {
  return {
    type: COPY_FLAGS_SUCCESS,
    payload: response,
  };
}

export function copyFlags(flagIds: number[], project: Project, isPrivate: boolean) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch(initiateCopyFlagsRequest(flagIds, project.id, isPrivate));

    return webApiClient
      .copyFlags(flagIds, project.id, isPrivate)
      .then((response: boolean) => {
        dispatch(copyFlagsRequestSuccess(response));

        // Reload current lastSaveTarget from configuration
        dispatch(loadUserParams());

        notify.info(
          {
            title: selMessage({ id: "flag.copy.successTitle", values: { flagsCount: flagIds.length } }),
            children: buildCopyFlagsMessage(flagIds.length, project),
          },
          { autoClose: 10000 },
        );

        return true;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: COPY_FLAGS_FAILURE,
          alertTitle: "Copy Flags Failed",
          alertMessage: `The flags could not be copied in our database`,
        }),
      );
  };
}

export function validateFlagsAgainstProjectUserGroup(projectId: number, flagIds: number[]) {
  return (_dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.validateFlagsAgainstProjectUserGroup(projectId, flagIds);
  };
}
